import React, {useEffect, useState} from 'react';
import {Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tab, Tabs, Tooltip, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {retrieveCurrentSchool, retrieveProfile} from './ProfilesSlice';
import {Link, Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {isTabConfirmed, outstandingTabs, unconfirmedOnTab} from './const/outstandingTabs';
import {HEADER_BOX_SX} from './const/headerBox';
import ProfileStatus, {onRollStatus, photoLineSx, statusPhotoLineSx} from './Components/ProfileStatus';
import PROFILE_TYPE from '../../../constants/profileTypes';
import {tabs} from './const/ProfileDetailTabs';
import {toTestId} from '../../../utility/testIdUtil';
import useUser from '../../../utility/hooks/useUser';
import {createSelector} from '@reduxjs/toolkit';
import {QueryStatus} from '@reduxjs/toolkit/query';
import {getRouteMatchPath} from '../../../utility/routeUtil';
import routes from '../../../routes/routes';
import {resetTableState} from '../../../store/tableSlice';
import {useGetFormsByStudentQuery} from '../../../api/services/formRequestsService';
import {FORM_REQUEST_STATUSES} from '../../../constants/formRequestStatuses';
import FILTER_OPERATIONS from '../../../constants/filterOperations';
import useMediaQuery from '@mui/material/useMediaQuery';
import {PhoneNextSection, PhoneSectionBar} from './Components/PhoneSections';
import {useAppHeaderHeight} from '../../../utility/hooks/useStickyBarOffset';

// Maria, 19 October 2026: the photo box and the names box share the width of the page equally. When the names box needs
// more room to keep the home address on one line, it takes it from the photo box, down to the width the photo box
// needs. Only then does the address go onto a second line. On a phone or small tablet the boxes are one under another.
const naturalWidth = element => {
    const {flex, width} = element.style;
    element.style.flex = '0 0 auto';
    element.style.width = 'max-content';
    const needed = element.offsetWidth;
    element.style.flex = flex;
    element.style.width = width;
    return needed;
};
const shareHeaderRow = row => {
    const [photoBox, namesBox] = row.children;
    if (!photoBox || !namesBox) return;
    photoBox.style.flex = '';
    namesBox.style.flex = '';
    row.style.flexDirection = '';
    if (window.innerWidth < 900) return;
    const room = row.clientWidth;
    const photoNeeds = naturalWidth(photoBox);
    // the narrowest the names box can be, with the address on as many lines as it takes
    const {width} = namesBox.style;
    namesBox.style.width = 'min-content';
    const namesLeast = namesBox.offsetWidth;
    namesBox.style.width = width;
    // too narrow for both side by side: one under the other rather than a sideways scroll
    if (photoNeeds + namesLeast > room) {
        row.style.flexDirection = 'column';
        return;
    }
    // Maria, 19 October 2026: the two tables side by side with no gap, so the names start straight after the photo
    // box's details and the spare room is at the end of the names
    const photoWidth = photoNeeds;
    photoBox.style.flex = `0 0 ${Math.floor(photoWidth)}px`;
    namesBox.style.flex = `0 0 ${Math.floor(room - photoWidth)}px`;
};
const shareHeaderRowRef = row => {
    if (!row || row.dataset.shared || typeof ResizeObserver === 'undefined') return;
    row.dataset.shared = 'on';
    let frame = 0;
    const run = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => shareHeaderRow(row));
    };
    new ResizeObserver(run).observe(row);
    new MutationObserver(run).observe(row, {childList: true, subtree: true, characterData: true});
    run();
};

const selectQueriesAndMutations = state => ({
    queries: state.api.queries,
    mutations: state.api.mutations,
});

// Medicine at School saves a field when the person leaves it. That save, and the list reloading
// after it, must not cover the page, or the button the person is clicking at that moment is lost
// (AD-2648). Loading the list the first time still covers it.
const SAVES_WITHOUT_COVER = ['updateStudentMedicine', 'getStudentMedicines'];
const coversThePage = (request) => request.status === QueryStatus.pending &&
    !(SAVES_WITHOUT_COVER.includes(request.endpointName) && (request.data !== undefined || request.endpointName === 'updateStudentMedicine'));

const selectIsSomeQueryPending = createSelector(
    [selectQueriesAndMutations],
    ({
         queries,
         mutations
     }) => {
        for (const query of Object.values(queries)) {
            if (coversThePage(query)) {
                return true;
            }
        }

        for (const mutation of Object.values(mutations)) {
            if (coversThePage(mutation)) {
                return true;
            }
        }

        return false;
    }
);

// Maria, 17 October 2026: tabs like a card index. White tabs with a blue outline and black words; the open tab is
// blue with white words and joins the blue line under the row of tabs.
// Maria, 17 October 2026: no wasted space in the profile. Inputs, ticks and cards are smaller and every tab uses the
// full width of the page.
export const compactProfileTheme = createTheme({
    // Maria, 18 October 2026: slightly smaller text with tighter lines in the introductions at the top of each section
    typography: {body1: {fontSize: '0.875rem', lineHeight: 1.25}},
    components: {
        MuiContainer: {defaultProps: {maxWidth: false, disableGutters: true}},
        MuiTextField: {defaultProps: {size: 'small'}},
        MuiFormControl: {defaultProps: {size: 'small'}},
        MuiAutocomplete: {defaultProps: {size: 'small'}},
        // Maria, 18 October 2026: one text size across the profile pages, so ticks, boxes, buttons and tables
        // match the introductions
        MuiCheckbox: {defaultProps: {size: 'small'}, styleOverrides: {root: {padding: 4}}},
        MuiInputBase: {styleOverrides: {root: {fontSize: '0.875rem'}}},
        MuiInputLabel: {styleOverrides: {root: {fontSize: '0.875rem'}}},
        MuiMenuItem: {styleOverrides: {root: {fontSize: '0.875rem'}}},
        MuiButton: {styleOverrides: {root: {fontSize: '0.8125rem'}}},
        MuiTableCell: {styleOverrides: {root: {fontSize: '0.875rem'}}},
        MuiFormControlLabel: {styleOverrides: {label: {fontSize: '0.875rem'}}},
        MuiRadio: {styleOverrides: {root: {padding: 4}}},
        MuiCardContent: {styleOverrides: {root: {padding: 16, '&:last-child': {paddingBottom: 16}}}}
    }
});

// Maria, 18 October 2026: on every tab except Basic, the student's name and the tab's title on one line,
// 'Helen Allcroft - Contacts', in place of a second heading on the tab.
const TAB_TITLES = {
    contact: 'Contacts',
    background: 'Cultural Information',
    medical: 'Medical Information',
    sen: 'Special Educational Needs and Disabilities',
    dietary: 'Dietary Needs and Food Allergies',
    welfare: 'Welfare and Pupil Premium Information',
    consents: 'Parental Consents',
    transport: 'Home-to-School Transport',
    documentation: 'Documents',
    forms: 'Forms',
    groups: 'Groups'
};

// Maria, 18 October 2026: on a smaller screen the tabs go onto more rows, with the same number of tabs on each row, so
// they always fill the width and every name can be read. 'rows' lists [the widest screen, tabs on each row].
export const tabsPerRow = rows => Object.fromEntries(rows.map(([maxWidth, perRow]) => [
    `@media (max-width: ${maxWidth}px)`,
    {'& .MuiTab-root, & .MuiTab-root.Mui-selected': {flex: `0 0 calc((100% - ${(perRow - 1) * 3}px) / ${perRow})`}}
]));

export const PROFILE_TABS_SX = {
    minHeight: 0,
    flex: 1,
    // Maria, 17 October 2026: the tabs fill the width of the page
    '& .MuiTabs-flexContainer': {flexWrap: 'wrap', gap: '3px', justifyContent: 'flex-start', alignItems: 'flex-end'},
    '& .MuiTab-root, & .MuiTab-root.Mui-selected': {flex: '1 1 0'},
    // Maria, 19 October 2026: rows of six below 1200px (a tablet), four on a phone; a laptop 1200px wide or more keeps the
    // tabs on one row as before
    ...tabsPerRow([[1199, 6], [760, 4], [480, 3]]),
    '& .MuiTab-root': {
        // Maria, 17 October 2026: deeper tabs, with room for 'UNCONFIRMED' under the name
        minHeight: 46,
        minWidth: 0,
        px: 1.25,
        pt: '5px',
        pb: '4px',
        // every tab name on the same line, at the top of its tab
        justifyContent: 'flex-start',
        textTransform: 'none',
        fontSize: 15,
        fontWeight: 500,
        // Maria, 17 October 2026: softer tabs, with thin pale borders like the white boxes below them
        color: '#24313f',
        opacity: 1,
        backgroundColor: '#fff',
        border: '1px solid #c5d3e6',
        borderBottom: 'none',
        borderRadius: '8px 8px 0 0',
        '&:hover': {backgroundColor: '#f3f7fc'}
    },
    // Maria, 18 October 2026: the open tab light blue with black text
    '& .MuiTab-root.Mui-selected': {
        color: '#000',
        fontWeight: 600,
        backgroundColor: '#d6e6f8',
        borderColor: '#186BBE',
        // the open tab is 4px taller, so its name starts 4px lower to stay level with the others
        minHeight: 50,
        pt: '9px'
    }
};

const StudentProfileDetails = () => {
    const isProfileInfoLoading = useSelector(state => state.profilesInfo.isLoading);
    const selectedProfile = useSelector(state => state.profilesInfo.selectedProfile);
    const updatingSuccessful = useSelector(state => state.profilesInfo.updatingSuccessful);
    const {studentId} = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {user} = useUser();
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const [profileTabs, setProfileTabs] = useState([]);
    const isSomeQueryPending = useSelector(selectIsSomeQueryPending);
    const {pathname} = useLocation();
    const path = getRouteMatchPath(routes, pathname);
    const isSchoolSwitching = useSelector(state => state.application.isSchoolSwitching);

    useEffect(() => {
        dispatch(retrieveCurrentSchool());

        return () => {
            dispatch(resetTableState(path));
        };
    }, [dispatch]);

    useEffect(() => {
        if (selectedProfile.id) {
            setProfileTabs(tabs
                .filter(tab => tab.shouldRender(
                    user.permissions,
                    user.parentPermissions,
                    selectedProfile.personType,
                    selectedProfile.isParentOfStudent,
                    user.roles
                ))
                .map(tab => typeof tab.label === 'function'
                    ? {
                        ...tab,
                        label: tab.label(selectedProfile.personType)
                    }
                    : tab)
            );
        }
    }, [selectedProfile.id, user.permissions.length, user.parentPermissions, selectedProfile.isParentOfStudent]);

    useEffect(() => {
        if (profileTabs.length === 0) return;

        const clean = pathname.replace(/\/+$/, '');
        const parts = clean.split('/');
        const pIndex = parts.indexOf('profiles');

        const segment = pIndex !== -1 && parts.length > pIndex + 3
            ? parts[pIndex + 3]
            : '';

        const id = profileTabs.findIndex(t => t.route === segment);
        if (id !== -1) {
            setCurrentTabIndex(id);
            return;
        }

        if (isSchoolSwitching) {
            return;
        }

        navigate(`${clean}/${profileTabs[0].route}`, { replace: true });
        setCurrentTabIndex(0);
    }, [pathname, profileTabs, navigate, isSchoolSwitching]);

    useEffect(() => {
        if (!studentId) {
            return undefined;
        }

        let cancelled = false;

        dispatch(retrieveProfile({studentId}))
            .unwrap()
            .catch(() => {
                if (!cancelled) {
                    navigate('/profiles', {replace: true});
                }
            });

        return () => {
            cancelled = true;
        };
    }, [dispatch, studentId, updatingSuccessful, navigate]);

    const [tabPopUp, setTabPopUp] = useState([]);
    const [poppedFor, setPoppedFor] = useState(null);
    useEffect(() => {
        const route = profileTabs[currentTabIndex]?.route;
        if (!selectedProfile.id || !route || poppedFor === `${selectedProfile.id}/${route}`) return;
        setPoppedFor(`${selectedProfile.id}/${route}`);
        setTabPopUp(unconfirmedOnTab(selectedProfile, route).length > 1 ? unconfirmedOnTab(selectedProfile, route) : []);
    }, [currentTabIndex, selectedProfile.id, selectedProfile.needsConfirmation]);

    const handleChange = (event, newValue) => {
        setCurrentTabIndex(newValue);
        navigate(profileTabs[newValue].route);
    };

    const currentRoute = profileTabs[currentTabIndex]?.route;
    const isBasicTab = currentRoute === 'basic';

    // Maria, 18 October 2026: the number of forms awaiting completion, under the name of the Forms tab
    const hasFormsTab = profileTabs.some(tab => tab.route === 'forms');
    // 'Created' and 'Awaiting submission' are both forms not yet completed
    const awaitingQuery = status => ({
        studentId,
        pageSize: 50,
        pageNumber: 0,
        filters: [{propertyName: 'status', value: status, operation: FILTER_OPERATIONS.EQUALS}]
    });
    const {data: formsSent} = useGetFormsByStudentQuery(awaitingQuery(FORM_REQUEST_STATUSES.sent), {skip: !studentId || !hasFormsTab});
    const {data: formsCreated} = useGetFormsByStudentQuery(awaitingQuery(FORM_REQUEST_STATUSES.created), {skip: !studentId || !hasFormsTab});
    const formsAwaiting = {items: [...(formsCreated?.items ?? []), ...(formsSent?.items ?? [])]};
    const formsAwaitingCount = (formsSent?.totalCount ?? 0) + (formsCreated?.totalCount ?? 0);
    const formsAwaitingNames = [...new Set((formsAwaiting?.items ?? []).map(item => item.form?.name).filter(Boolean))];

    // Maria, 19 October 2026: on a phone, one bar with the open section in place of the rows of tabs
    const isPhone = useMediaQuery('(max-width:599.95px)');
    // the bar stops under the Admicity header as the page scrolls
    useAppHeaderHeight(isPhone);
    const statusOf = tab => {
        if (outstandingTabs(selectedProfile, [tab]).length > 0) return {text: 'Unconfirmed', colour: 'red'};
        if (tab.route === 'forms' && formsAwaitingCount > 0) return {text: `${formsAwaitingCount} to complete`, colour: 'red'};
        if (isTabConfirmed(selectedProfile, tab.route)) return {text: 'Confirmed', colour: 'green'};
        return null;
    };
    const selectSection = index => handleChange(null, index);

    return (
        <>
            {/* Maria, 17 October 2026: the tabs directly under the main menu, from the left edge. Tabs like a card
                index, wrapping onto more rows on a phone. */}
            {/* Maria, 17 October 2026: the tab row stays at the top of the screen while the page scrolls */}
            <Stack
                direction="row"
                alignItems="flex-end"
                sx={{mt: 0, borderBottom: '2px solid #d6e6f8', position: 'sticky', top: isPhone ? 'var(--app-header-height, 0px)' : 0, zIndex: 5, backgroundColor: '#F0F4F8', pt: 0.5}}
            >
                {selectedProfile.id && profileTabs.length > 0 && isPhone && (
                    <PhoneSectionBar tabs={profileTabs} index={currentTabIndex} statusOf={statusOf} onSelect={selectSection}/>
                )}
                {selectedProfile.id && profileTabs.length > 0 && !isPhone && (
                    <Tabs
                        value={currentTabIndex}
                        onChange={handleChange}
                        textColor="inherit"
                        TabIndicatorProps={{sx: {display: 'none'}}}
                        sx={PROFILE_TABS_SX}
                    >
                        {profileTabs.map((tab, index) => (
                            <Tab
                                key={index}
                                tabIndex={index}
                                data-testid={toTestId('profile-tab', tab.route)}
                                label={(
                                    <Stack alignItems="center" gap={0.25}>
                                        <span>{tab.label}</span>
                                        {/* Maria, 17 October 2026: 'UNCONFIRMED' in red under the name of a tab with
                                            information still to be completed or confirmed */}
                                        {outstandingTabs(selectedProfile, [tab]).length > 0 && (
                                            <Tooltip
                                                arrow
                                                placement="bottom"
                                                title={
                                                    <Box sx={{p: 0.5}}>
                                                        <Typography variant="body2" fontWeight={700} sx={{mb: 0.25}}>
                                                            Awaiting completion or confirmation:
                                                        </Typography>
                                                        {outstandingTabs(selectedProfile, [tab])[0].missing.map(item => (
                                                            <Typography key={item} variant="body2">{item}</Typography>
                                                        ))}
                                                    </Box>
                                                }
                                            >
                                                <Box
                                                    component="span"
                                                    data-testid={toTestId('unconfirmed-tab', tab.route)}
                                                    sx={{color: '#c62828', backgroundColor: 'transparent', fontSize: 11, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1.4, px: 0.5, borderRadius: 0.5}}
                                                >
                                                    Unconfirmed
                                                </Box>
                                            </Tooltip>
                                        )}
                                        {tab.route === 'forms' && formsAwaitingCount > 0 && (
                                            <Tooltip
                                                arrow
                                                placement="bottom"
                                                title={
                                                    <Box sx={{p: 0.5}}>
                                                        <Typography variant="body2" fontWeight={700} sx={{mb: 0.25}}>
                                                            Awaiting completion:
                                                        </Typography>
                                                        {formsAwaitingNames.map(name => (
                                                            <Typography key={name} variant="body2">{name}</Typography>
                                                        ))}
                                                    </Box>
                                                }
                                            >
                                                <Box
                                                    component="span"
                                                    data-testid="forms-awaiting-tab"
                                                    sx={{color: '#c62828', backgroundColor: 'transparent', fontSize: 11, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1.4, px: 0.5, borderRadius: 0.5, whiteSpace: 'nowrap'}}
                                                >
                                                    {/* Maria, 18 October 2026: '2 TO COMPLETE' on one line, with the forms named in the hover */}
                                                    {formsAwaitingCount} To complete
                                                </Box>
                                            </Tooltip>
                                        )}
                                        {/* Maria, 17 October 2026: 'CONFIRMED' in green when everything on the tab is confirmed */}
                                        {isTabConfirmed(selectedProfile, tab.route) && (
                                            <Box
                                                component="span"
                                                data-testid={toTestId('confirmed-tab', tab.route)}
                                                sx={{color: '#2e7d32', backgroundColor: 'transparent', fontSize: 11, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1.4, px: 0.5, borderRadius: 0.5}}
                                            >
                                                Confirmed
                                            </Box>
                                        )}
                                    </Stack>
                                )}
                                component={Link}
                                to={tab.route}
                                disableRipple
                            />
                        ))}
                    </Tabs>
                )}
            </Stack>
            {/* Maria, 17 October 2026: the student's name under the tabs, except on Basic, where the name is beside
                the photo. 'UNCONFIRMED' on the tabs takes the place of the list of outstanding sections. */}
            {selectedProfile.id && !isBasicTab && (
                <Typography variant="h5" component="h1" fontWeight={600} sx={{mt: 1}} data-testid="profile-header-line">
                    {selectedProfile.name} {selectedProfile.surname}{TAB_TITLES[currentRoute] ? ` - ${TAB_TITLES[currentRoute]}` : ''}
                </Typography>
            )}
            {selectedProfile.id && isBasicTab && (
                // Maria, 18 October 2026: on a phone or a small iPad the boxes go one under another.
                // Maria, 19 October 2026: one white box across the page, so the details read as one long table
                <Stack ref={shareHeaderRowRef} direction={{xs: 'column', md: 'row'}} alignItems="stretch" gap={0} sx={{
                    mt: 1,
                    ...HEADER_BOX_SX,
                    px: 0,
                    py: 0,
                    '& [data-testid="profile-header-box"], & [data-testid="header-student-details"]': {border: 'none', borderRadius: 0, backgroundColor: 'transparent'},
                    '& [data-testid="header-student-details"]': {pl: {xs: 2, md: 0}}
                }}>
                    {/* Maria, 17 October 2026: the photo, name, year group, form, boarder status and UPN in their own
                        white box, matching the previous school box beside it. */}
                    <Stack direction="row" gap={2.5} sx={{
                        ...HEADER_BOX_SX,
                        // Maria, 19 October 2026: the photo box and the names box are the same width; the photo box is
                        // never narrower than its longest line, so nothing in it wraps
                        flex: {md: '1 1 0'},
                        minWidth: {xs: 0, md: 'max-content'},
                        boxSizing: 'border-box'
                    }} data-testid="profile-header-box">
                    {/* Maria, 17 October 2026: an applicant's status above the photo, square and the same width. */}
                    {/* Maria, 19 October 2026: a smaller photo on a phone, so the details beside it have room */}
                    <Stack sx={{width: {xs: 96, md: 124}, flexShrink: 0, pt: '6px', pb: '4px'}}>
                        {/* Maria, 19 October 2026: a child on roll has the 'On Roll' badge (or 'Admitted' before the date
                            of admission), as on the profile card */}
                        {(selectedProfile.personType === PROFILE_TYPE.APPLICATION || selectedProfile.personType === PROFILE_TYPE.ENQUIRY ||
                            selectedProfile.personType === PROFILE_TYPE.STUDENT) && (
                            <Box
                                data-testid="profile-status-pill"
                                // Maria, 19 October 2026: a bigger font in the status above the photo
                                sx={{'& > div': {width: '100%', boxSizing: 'border-box', borderRadius: 0, px: 0.5, py: 0.25}, '& .MuiTypography-root': {fontSize: '15px !important'}}}
                            >
                                <ProfileStatus
                                    {...(selectedProfile.personType === PROFILE_TYPE.STUDENT
                                        ? onRollStatus(selectedProfile.dateOfAdmission)
                                        : {
                                            status: selectedProfile.personType === PROFILE_TYPE.ENQUIRY ? 'Enquiry' : selectedProfile.status,
                                            statusCategory: selectedProfile.personType === PROFILE_TYPE.ENQUIRY ? 'Enquiry' : selectedProfile.statusCategory
                                        })}
                                />
                            </Box>
                        )}
                        {/* Maria, 19 October 2026: the 3 by 4 shape of a school photo */}
                        <Box sx={{position: 'relative', display: 'flex', aspectRatio: '3 / 4'}}>
                            <Avatar
                                variant="square"
                                sx={{
                                    width: {xs: 96, md: 124},
                                    height: '100%',
                                    // Maria, 19 October 2026: a fine line round the photo in the colour of the status badge
                                    ...(selectedProfile.personType === PROFILE_TYPE.APPLICATION || selectedProfile.personType === PROFILE_TYPE.ENQUIRY
                                        ? statusPhotoLineSx(selectedProfile.personType === PROFILE_TYPE.ENQUIRY ? 'Enquiry' : selectedProfile.statusCategory)
                                        : photoLineSx('#1E8FA0'))
                                }}
                                alt={`${selectedProfile.name} ${selectedProfile.surname}`}
                                src={selectedProfile.photo ? `data:image/jpeg;base64,${selectedProfile.photo}` : undefined}
                                data-testid="profile-photo"
                            />
                            {/* While an applicant's profile is being edited, BasicInfo puts the photo upload here, over
                                the photo, so it is changed in the same place. */}
                            <Box
                                id="profile-photo-slot"
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    '&:empty': {display: 'none'},
                                    '& > div, & .MuiAvatar-root': {width: '100% !important', height: '100% !important', mb: '0 !important'}
                                }}
                            />
                        </Box>
                    </Stack>
                    {/* Maria, 17 October 2026: the last line lines up with the bottom of the photo. */}
                        {/* Maria, 19 October 2026: on a phone or small tablet, only the space beside the photo */}
                        <Box sx={{minHeight: 124, display: 'flex', flexDirection: 'column', width: {xs: 'auto', md: 'max-content'}, flex: {xs: '1 1 0', md: 'none'}, minWidth: {xs: 0, md: 'auto'}, maxWidth: '100%'}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={2} flexWrap="wrap">
                                <Typography variant="h5" component="h1" fontWeight={600}>
                                    {selectedProfile.name} {selectedProfile.surname}
                                </Typography>
                            </Stack>
                        {/* BasicInfo fills this space with the year group, form, boarder status and UPN. */}
                        <Box id="profile-header-details" sx={{mt: 0.75}}/>
                        </Box>
                    </Stack>
                        {/* Maria, 17 October 2026: the previous school details, and an applicant's admissions
                            categories, beside the photo box. BasicInfo fills this space. */}
                        <Box id="profile-header-registration" sx={{display: 'flex', flex: 1, minWidth: 0}}/>
                </Stack>
            )}
            <Box
                position="relative"
                minHeight={40}
                marginTop={1}
                display="flex"
                flexGrow={1}
                flexDirection="column"
            >
                {/* Maria, 17 October 2026: on opening a tab with more than one section, a pop-up lists the sections
                    on it still to be completed or confirmed. */}
                <Dialog open={tabPopUp.length > 0} onClose={() => setTabPopUp([])} maxWidth="xs" fullWidth data-testid="unconfirmed-on-tab">
                    <DialogTitle sx={{pb: 1}}>Awaiting completion or confirmation</DialogTitle>
                    <DialogContent>
                        <Typography sx={{mb: 1}}>The following sections on this tab still need to be completed or confirmed:</Typography>
                        {tabPopUp.map(item => <Typography key={item} sx={{color: '#d32f2f', fontWeight: 600}}>{item}</Typography>)}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={() => setTabPopUp([])}>OK</Button>
                    </DialogActions>
                </Dialog>
                <ThemeProvider theme={compactProfileTheme}>
                    <Box sx={{fontSize: '0.875rem', lineHeight: 1.25}}>
                        <Outlet context={{key: path}}/>
                    </Box>
                </ThemeProvider>
                {isPhone && selectedProfile.id && (
                    <PhoneNextSection tabs={profileTabs} index={currentTabIndex} onSelect={selectSection}/>
                )}
                {(
                    ((isSomeQueryPending || isProfileInfoLoading) && profileTabs[currentTabIndex]?.showLoadingSpinner) ||
                    (isProfileInfoLoading && !selectedProfile.id)
                ) && (
                    <>
                        <Box
                            display="block"
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            sx={{
                                backgroundColor: 'rgb(250, 250, 251)',
                                zIndex: 1
                            }}
                        />
                        <Box
                            position="fixed"
                            top="55%"
                            left="50%"
                            sx={{
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1
                            }}
                        >
                            <CircularProgress size={40}/>
                        </Box>
                    </>
                )}
            </Box>
        </>);
};

export default StudentProfileDetails;
